// YNX — INVOICES & QUOTES MODULE (devis & factures)
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Download, FileText, FileType } from 'lucide-react';
import {
  listInvoices, createInvoice, deleteInvoice, listClients, downloadInvoiceDocx,
} from '../../lib/biz';

const EMPTY_LINE = { description: '', quantity: 1, unit_price: 0, vat_rate: 20 };

// Stable IDs for invoice lines so React reconciliation doesn't re-mount
// inputs when a line is added/removed (fixes lost focus and stale state).
let _lineCounter = 0;
const newLine = () => ({ _key: `L${++_lineCounter}`, ...EMPTY_LINE });

const totalOf = (lines) => {
  const ht = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const vat = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * ((Number(l.vat_rate) || 0) / 100), 0);
  return { ht: ht.toFixed(2), vat: vat.toFixed(2), ttc: (ht + vat).toFixed(2) };
};

// ─── PDF generation (XSS-safe via Blob URL + DOMPurify-escaped user inputs) ─
const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const generatePdf = (doc, ws) => {
  const html = `<!doctype html><html><head><meta charset='utf-8'><title>${esc(doc.number)}</title>
<style>
  body { font-family: 'Helvetica', sans-serif; color: #1a2f4c; padding: 40px; max-width: 800px; margin: 0 auto; }
  header { display: flex; justify-content: space-between; border-bottom: 3px solid #fbbf24; padding-bottom: 20px; margin-bottom: 30px; }
  header h1 { margin: 0; font-size: 28px; color: #fbbf24; letter-spacing: 2px; }
  .ws-info { text-align: right; font-size: 12px; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
  .meta h3 { font-size: 11px; text-transform: uppercase; color: #94a3b8; margin: 0 0 6px; letter-spacing: 1px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
  th { background: #15263f; color: #f8fafc; text-align: left; padding: 10px; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
  td { padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
  td.num { text-align: right; }
  .totals { float: right; width: 280px; }
  .totals .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
  .totals .row.ttc { border-top: 2px solid #fbbf24; padding-top: 12px; margin-top: 8px; font-weight: bold; font-size: 16px; color: #fbbf24; }
  .legal { clear: both; padding-top: 30px; font-size: 10px; color: #94a3b8; line-height: 1.6; }
  .actions { margin-bottom: 20px; }
  @media print { .actions { display: none; } body { padding: 20px; } }
</style></head><body>
  <div class='actions'>
    <button onclick='window.print()' style='padding:10px 20px;background:#fbbf24;color:#0a1426;border:none;border-radius:6px;font-weight:bold;cursor:pointer;'>📥 Imprimer / Enregistrer en PDF</button>
  </div>
  <header>
    <div>
      <h1>${doc.type === 'quote' ? 'DEVIS' : 'FACTURE'}</h1>
      <div style='font-size: 14px; color: #94a3b8; margin-top: 4px;'>N° ${esc(doc.number)}</div>
      <div style='font-size: 13px; margin-top: 4px;'>Date : ${esc(doc.issue_date || '')}</div>
      ${doc.due_date ? `<div style='font-size: 13px;'>Échéance : ${esc(doc.due_date)}</div>` : ''}
    </div>
    <div class='ws-info'>
      <strong style='font-size: 16px;'>${esc(ws?.name || '')}</strong><br>
      ${ws?.address ? esc(ws.address) + '<br>' : ''}
      ${ws?.phone ? 'Tél : ' + esc(ws.phone) + '<br>' : ''}
      ${ws?.owner_email ? esc(ws.owner_email) + '<br>' : ''}
      ${ws?.siret ? 'SIRET : ' + esc(ws.siret) + '<br>' : ''}
      ${ws?.vat_number ? 'TVA : ' + esc(ws.vat_number) : ''}
    </div>
  </header>

  <div class='meta'>
    <div>
      <h3>Émis par</h3>
      <div><strong>${esc(ws?.name || '')}</strong></div>
      ${ws?.address ? `<div>${esc(ws.address)}</div>` : ''}
    </div>
    <div>
      <h3>Destinataire</h3>
      <div><strong>${esc(doc.client_snapshot?.name || doc.client_snapshot?.company || '')}</strong></div>
      ${doc.client_snapshot?.company ? `<div>${esc(doc.client_snapshot.company)}</div>` : ''}
      ${doc.client_snapshot?.address ? `<div>${esc(doc.client_snapshot.address)}</div>` : ''}
      ${doc.client_snapshot?.email ? `<div>${esc(doc.client_snapshot.email)}</div>` : ''}
    </div>
  </div>

  <table>
    <thead><tr>
      <th>Désignation</th><th class='num'>Qté</th><th class='num'>PU HT</th><th class='num'>TVA</th><th class='num'>Total HT</th>
    </tr></thead>
    <tbody>
      ${(doc.lines || []).map(l => `<tr>
        <td>${esc(l.description || '')}</td>
        <td class='num'>${esc(l.quantity)}</td>
        <td class='num'>${Number(l.unit_price).toFixed(2)} €</td>
        <td class='num'>${esc(l.vat_rate)}%</td>
        <td class='num'>${(Number(l.quantity) * Number(l.unit_price)).toFixed(2)} €</td>
      </tr>`).join('')}
    </tbody>
  </table>

  <div class='totals'>
    <div class='row'><span>Total HT</span><span>${Number(doc.total_ht).toFixed(2)} €</span></div>
    <div class='row'><span>TVA</span><span>${Number(doc.total_vat).toFixed(2)} €</span></div>
    <div class='row ttc'><span>TOTAL TTC</span><span>${Number(doc.total_ttc).toFixed(2)} €</span></div>
  </div>

  ${doc.notes ? `<div style='clear:both; padding-top: 20px; padding-bottom: 20px; font-size: 13px;'><strong>Notes :</strong><br>${esc(doc.notes).replace(/\n/g, '<br>')}</div>` : '<div style="clear:both;"></div>'}

  <div class='legal'>
    ${doc.type === 'invoice' ? 'En cas de retard de paiement, des pénalités de retard au taux légal (3 fois le taux d\'intérêt légal en vigueur) seront appliquées. Indemnité forfaitaire pour frais de recouvrement : 40 €.' : 'Devis valable 30 jours à compter de la date d\'émission.'}
    <br><br>
    ${ws?.siret ? 'SIRET : ' + esc(ws.siret) + ' · ' : ''}${ws?.vat_number ? 'N° TVA : ' + esc(ws.vat_number) : ''}
    <br>
    Document généré par YNX · OS IA des entreprises · by Carl TACITA
  </div>
</body></html>`;
  // ── XSS-safe: open via Blob URL (sandboxed origin, cannot tamper with
  // parent window). User inputs are HTML-escaped via esc() above. ───────────
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank', 'width=900,height=1200');
  if (!w) {
    URL.revokeObjectURL(url);
    alert('Veuillez autoriser les pop-ups pour télécharger le PDF.');
    return;
  }
  // Free the blob once the popup has loaded it
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const InvoicesModule = ({ workspace }) => {
  const [type, setType] = useState('invoice');
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'invoice', client_id: '', client_snapshot: null,
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: '', lines: [newLine()], notes: '',
  });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [docs, cls] = await Promise.all([listInvoices(), listClients()]);
      setItems(docs);
      setClients(cls);
    } catch (e) { console.warn('YNX invoices: refresh failed', e); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const updateLine = (i, patch) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], ...patch };
    setForm({ ...form, lines });
  };

  const t = totalOf(form.lines);

  const save = async () => {
    if (form.lines.length === 0 || !form.lines[0].description) {
      alert('Ajoutez au moins une ligne avec description.');
      return;
    }
    setBusy(true);
    try {
      const snap = clients.find((c) => c.id === form.client_id) || null;
      await createInvoice({
        ...form,
        type,
        client_snapshot: snap ? { name: snap.name, company: snap.company, address: snap.address, email: snap.email } : null,
      });
      setForm({
        type, client_id: '', client_snapshot: null,
        issue_date: new Date().toISOString().slice(0, 10),
        due_date: '', lines: [newLine()], notes: '',
      });
      setShowForm(false);
      await refresh();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await deleteInvoice(id);
    await refresh();
  };

  const filtered = items.filter((d) => d.type === type);

  return (
    <div className='biz-module'>
      <header className='biz-module-head'>
        <h2>🧾 Devis & Factures</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className='biz-segmented'>
            <button className={type === 'invoice' ? 'active' : ''} onClick={() => setType('invoice')}>Factures</button>
            <button className={type === 'quote' ? 'active' : ''} onClick={() => setType('quote')}>Devis</button>
          </div>
          <button className='biz-btn-primary sm' onClick={() => { setForm({ ...form, type }); setShowForm(true); }} data-testid='inv-add-btn'>
            <Plus size={14} /> Nouveau {type === 'quote' ? 'devis' : 'facture'}
          </button>
        </div>
      </header>

      {filtered.length === 0 && <div className='biz-empty'>Aucun document. Créez-en un pour commencer.</div>}

      <table className='biz-table'>
        <thead>
          <tr><th>N°</th><th>Date</th><th>Client</th><th>Total TTC</th><th>Statut</th><th></th></tr>
        </thead>
        <tbody>
          {filtered.map((d) => (
            <tr key={d.id}>
              <td><FileText size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {d.number}</td>
              <td>{d.issue_date}</td>
              <td>{d.client_snapshot?.name || d.client_snapshot?.company || '—'}</td>
              <td><strong>{Number(d.total_ttc).toFixed(2)} €</strong></td>
              <td><span className={`biz-status ${d.status}`}>{d.status}</span></td>
              <td style={{ textAlign: 'right' }}>
                <button className='biz-icon-btn' onClick={() => generatePdf(d, workspace)} title='Voir / PDF'>
                  <Download size={13} />
                </button>
                <button
                  className='biz-icon-btn'
                  onClick={async () => {
                    try { await downloadInvoiceDocx(d.id, `${d.number || d.id}.docx`); }
                    catch (err) { alert('Export Word impossible : ' + err.message); }
                  }}
                  title='Télécharger en Word (.docx)'
                  data-testid={`inv-docx-${d.id}`}
                >
                  <FileType size={13} />
                </button>
                <button className='biz-icon-btn danger' onClick={() => remove(d.id)} title='Supprimer'>
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className='biz-modal lg'>
            <header className='biz-modal-head'>
              <h3>Nouveau {type === 'quote' ? 'devis' : 'facture'}</h3>
              <button className='biz-icon-btn' onClick={() => setShowForm(false)}><X size={14} /></button>
            </header>
            <div className='biz-modal-body'>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label className='biz-label'>Client</label>
                  <select className='biz-input' name='inv-client' value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                    <option value=''>— Aucun (saisie libre) —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className='biz-label'>Date d'émission</label>
                  <input className='biz-input' name='inv-issue' type='date' value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
                </div>
                <div>
                  <label className='biz-label'>Échéance</label>
                  <input className='biz-input' name='inv-due' type='date' value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>

              <h4 className='biz-modal-sect'>Lignes</h4>
              <table className='biz-table sm'>
                <thead>
                  <tr><th>Désignation</th><th>Qté</th><th>PU HT</th><th>TVA</th><th></th></tr>
                </thead>
                <tbody>
                  {form.lines.map((l, i) => (
                    <tr key={l._key || `idx-${i}`}>
                      <td><input className='biz-input' name={`line-desc-${l._key}`} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder='Désignation' /></td>
                      <td style={{ width: 80 }}><input className='biz-input' name={`line-qty-${l._key}`} type='number' step='0.5' value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} /></td>
                      <td style={{ width: 100 }}><input className='biz-input' name={`line-price-${l._key}`} type='number' step='0.01' value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} /></td>
                      <td style={{ width: 80 }}>
                        <select className='biz-input' name={`line-vat-${l._key}`} value={l.vat_rate} onChange={(e) => updateLine(i, { vat_rate: e.target.value })}>
                          <option value='0'>0%</option>
                          <option value='5.5'>5.5%</option>
                          <option value='10'>10%</option>
                          <option value='20'>20%</option>
                        </select>
                      </td>
                      <td>
                        <button className='biz-icon-btn danger' onClick={() => setForm({ ...form, lines: form.lines.filter((line) => line._key !== l._key) })}>
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className='biz-btn-ghost sm' onClick={() => setForm({ ...form, lines: [...form.lines, newLine()] })}>
                <Plus size={12} /> Ajouter une ligne
              </button>

              <div className='biz-totals-summary'>
                <span>HT : <b>{t.ht} €</b></span>
                <span>TVA : <b>{t.vat} €</b></span>
                <span className='ttc'>TTC : <b>{t.ttc} €</b></span>
              </div>

              <label className='biz-label'>Notes / mentions complémentaires</label>
              <textarea className='biz-input' name='inv-notes' rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <footer className='biz-modal-foot'>
              <button className='biz-btn-ghost' onClick={() => setShowForm(false)}>Annuler</button>
              <button className='biz-btn-primary' onClick={save} disabled={busy} data-testid='inv-save-btn'>
                {busy ? 'Enregistrement…' : `Créer le ${type === 'quote' ? 'devis' : 'facture'}`}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};
